/* import __COLOCATED_TEMPLATE__ from './brand-dropdown.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';

import { inject as service } from '@ember/service';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type RouterService from '@ember/routing/router-service';
import type BrandService from 'embercom/services/brand-service';
import type Brand from 'embercom/models/brand';
import { getBrandDropdownLabel } from '../playground/settings/helpers';

export interface Args {
  brandId?: string | null;
  onSelectBrand: (brandId: string | null) => void;
  disabled?: boolean;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class BrandDropdown extends Component<Signature> {
  @service declare appService: AppService;
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare intl: any;
  @service declare router: RouterService;
  @service declare notificationsService: any;
  @tracked isDropdownOpen = false;
  @tracked showModal = false;
  @tracked isRunning = false;

  @service declare brandService: BrandService;

  private DEFAULT_BRAND_ID = '0';

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    // Load brands for the brand dropdown
    this.brandService.loadBrands();
  }

  get brands() {
    return this.brandService.brands;
  }

  get dropdownLabel() {
    return getBrandDropdownLabel({
      brandId: this.args.brandId,
      brands: this.brands,
      appName: this.appService.app.name,
      intl: this.intl,
      disabled: this.args.disabled,
      defaultBrandId: this.DEFAULT_BRAND_ID,
    });
  }

  get selectedBrandId() {
    let foundBrand = this.brands.find((brand) => brand.id === this.args.brandId);
    if (foundBrand) {
      return foundBrand.id;
    }
    return this.DEFAULT_BRAND_ID;
  }

  get brandsList() {
    let defaultBrandOption = {
      text: this.appService.app.name,
      value: this.DEFAULT_BRAND_ID,
    };

    // we filter out the default here because the store behaviour is unreliable
    // if there is only the default brand, it is present in the store findAll
    // but if there are more, due to ID type mismatch (default ID is string), the default brand is not present
    // so we handle it ourselves
    let customBrandOptions = this.brands
      .filter((brand) => !brand.isDefault)
      .map((brand: Brand) => ({
        text: brand.name,
        value: brand.id,
      }));

    return [defaultBrandOption, ...customBrandOptions].sort((a, b) => a.text.localeCompare(b.text));
  }

  @action onSelectBrand(brandId: string) {
    if (brandId === this.DEFAULT_BRAND_ID) {
      this.args.onSelectBrand(null);
    } else {
      this.args.onSelectBrand(brandId);
    }
  }

  @action redirectToBrandSettings() {
    let url = this.router.urlFor('apps.app.settings.workspace.brands', this.appService.app.id);
    safeWindowOpen(url, '_blank');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Settings::BrandDropdown': typeof BrandDropdown;
    'ai-agent/settings/brand-dropdown': typeof BrandDropdown;
  }
}
